export interface DropInSessionExpiryOptions {
  now?: Date;
  timeZone?: string | null;
}

type LocalDate = {
  year: number;
  month: number;
  day: number;
};

function localDate(date: Date, timeZone: string): LocalDate {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone,
    year: "numeric",
    month: "numeric",
    day: "numeric",
  }).formatToParts(date);
  const value = (type: Intl.DateTimeFormatPartTypes) =>
    Number(parts.find((part) => part.type === type)?.value);
  return {
    year: value("year"),
    month: value("month"),
    day: value("day"),
  };
}

function calendarDayDifference(start: LocalDate, end: LocalDate): number {
  const startDay = Date.UTC(start.year, start.month - 1, start.day);
  const endDay = Date.UTC(end.year, end.month - 1, end.day);
  return Math.round((endDay - startDay) / 86_400_000);
}

function remainingLabel(milliseconds: number): string {
  const totalMinutes = Math.max(0, Math.ceil(milliseconds / 60_000));
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  if (hours === 0) return `${minutes}m remaining`;
  if (minutes === 0) return `${hours}h remaining`;
  return `${hours}h ${minutes}m remaining`;
}

/**
 * Formats the backend-owned drop-in expiry in facility-local time. Remaining
 * duration uses elapsed instants, so DST transitions cannot distort it.
 */
export function formatDropInSessionExpiry(
  expiresAt: string | null | undefined,
  { now = new Date(), timeZone }: DropInSessionExpiryOptions,
): string | null {
  if (!expiresAt || !timeZone) return null;
  const expiry = new Date(expiresAt);
  if (
    Number.isNaN(expiry.getTime()) ||
    Number.isNaN(now.getTime()) ||
    expiry.getTime() <= now.getTime()
  ) {
    return null;
  }

  try {
    const dayDifference = calendarDayDifference(
      localDate(now, timeZone),
      localDate(expiry, timeZone),
    );
    const time = new Intl.DateTimeFormat("en-US", {
      timeZone,
      hour: "numeric",
      minute: "2-digit",
    }).format(expiry);
    const when =
      dayDifference === 0
        ? "today"
        : dayDifference === 1
          ? "tomorrow"
          : `on ${new Intl.DateTimeFormat("en-US", {
              timeZone,
              month: "short",
              day: "numeric",
            }).format(expiry)}`;
    return `Expires ${when} at ${time} (${remainingLabel(
      expiry.getTime() - now.getTime(),
    )})`;
  } catch {
    return null;
  }
}
